import { useAuth } from '../contexts/auth-context';
import { Button, Icon } from './ui';
import { fmtDateShort, fmtTime } from '../utils/format';
import { BaanWelcome, BaanPersonalShortcut } from './BaanWelcome';

function SetupGuide({ transponders, onNavigate }) {
  const steps = [
    { title: 'Account', detail: 'Je profiel is klaar.', complete: true },
    {
      title: 'Transponder',
      detail: transponders.length ? `${transponders.length} gekoppeld` : 'Koppel je transponder om je ritten te vinden.',
      complete: transponders.length > 0,
    },
    { title: 'Hartslag', detail: 'Optioneel: koppel een sporthorloge of sensor.', optional: true },
  ];

  return (
    <section className="personal-setup" aria-labelledby="personal-setup-title">
      <div className="personal-setup-art" aria-hidden="true">
        <svg viewBox="0 0 360 150" fill="none">
          <path d="M56 42h248a34 34 0 0 1 0 68H56a34 34 0 0 1 0-68Z" />
          <path d="M56 52h248a24 24 0 0 1 0 48H56a24 24 0 0 1 0-48Z" />
          <circle cx="92" cy="42" r="4" />
          <circle cx="303" cy="76" r="4" />
        </svg>
      </div>
      <div className="personal-setup-copy">
        <p className="personal-dashboard-eyebrow">JOUW BAAN · JOUW DATA</p>
        <h2 id="personal-setup-title">Haal meer uit elke ronde.</h2>
        <p>Koppel je transponder. Je ritten en persoonlijke voortgang staan daarna vanzelf bij elkaar.</p>
        <ol className="personal-setup-steps">
          {steps.map((step, index) => (
            <li key={step.title} className={step.complete ? 'is-complete' : ''}>
              <span className="personal-step-number">
                {step.complete ? <Icon name="check" size={14} /> : `0${index + 1}`}
              </span>
              <span><strong>{step.title}</strong><small>{step.detail}</small></span>
              {step.optional && <em>OPTIONEEL</em>}
            </li>
          ))}
        </ol>
        <Button variant="accent" onClick={() => onNavigate('profile')}>
          {transponders.length ? 'Koppelingen beheren' : 'Transponder koppelen'}
          <Icon name="chevronRight" size={16} />
        </Button>
      </div>
      <div className="personal-setup-caption"><span className="live-dot" /> DE MEENT · ALKMAAR</div>
    </section>
  );
}

function MetricCard({ label, value, unit, icon, tone, note }) {
  return (
    <article className="personal-metric">
      <div className="personal-metric-top">
        <span>{label}</span>
        <i className={`personal-metric-icon ${tone}`}><Icon name={icon} size={17} /></i>
      </div>
      <strong>{value}<small>{unit}</small></strong>
      <p>{note}</p>
    </article>
  );
}

export default function PersonalDashboard({
  sessions,
  statsMap,
  date,
  isToday,
  loading,
  error,
  updatedAt,
  onNavigate,
}) {
  const { loggedIn, loading: authLoading, user, transponders, owns } = useAuth();
  if (!loggedIn || authLoading) {
    return (
      <>
        <BaanWelcome onNavigate={onNavigate} />
        {!authLoading && <BaanPersonalShortcut sessions={sessions} date={date} onNavigate={onNavigate} />}
      </>
    );
  }

  const ownSessions = sessions.filter(session => owns(session.transponder));
  const ownStats = ownSessions.map(session => statsMap[session.transponder]).filter(Boolean);
  const totalLaps = ownStats.reduce((sum, stats) => sum + (stats.lapCount || 0), 0);
  const bestLapMs = ownStats.map(stats => stats.bestLapMs).filter(Number.isFinite).sort((a, b) => a - b)[0];
  const greetingName = user?.name?.trim()?.split(/\s+/)[0] || 'schaatser';
  const updateLabel = error
    ? 'Verbinding onderbroken'
    : updatedAt
      ? `Bijgewerkt om ${new Date(updatedAt).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Amsterdam' })}`
      : loading
        ? 'Gegevens ophalen…'
        : 'Wachten op baanactiviteit';

  return (
    <section className="personal-dashboard" aria-labelledby="personal-dashboard-title">
      <div className="personal-dashboard-heading">
        <div>
          <p className="personal-dashboard-eyebrow">
            {isToday ? 'VANDAAG OP DE MEENT' : date ? `BAANOVERZICHT · ${fmtDateShort(date).toUpperCase()}` : 'ICE PERFORMANCE'}
          </p>
          <h1 id="personal-dashboard-title">Welkom terug, {greetingName}</h1>
          <p className="personal-dashboard-subtitle">
            {ownSessions.length
              ? isToday ? 'Je ritten van vandaag, in één overzicht.' : `Je ritten van ${fmtDateShort(date)}, in één overzicht.`
              : 'Volg je ritten en vooruitgang op De Meent.'}
          </p>
        </div>
        <button className="personal-dashboard-refresh" onClick={() => onNavigate('myseason')} aria-label="Bekijk alle eigen ritten">
          <Icon name="chevronRight" size={18} />
        </button>
      </div>

      {transponders.length === 0 ? (
        <SetupGuide transponders={transponders} onNavigate={onNavigate} />
      ) : (
        <>
          <div className="personal-dashboard-banner">
            <div className="personal-dashboard-banner-copy">
              <span><i className="live-dot" /> DE MEENT · ALKMAAR</span>
              <h2>{ownSessions.length ? 'Sterke rondes. Goed bezig.' : 'Klaar voor je volgende rit?'}</h2>
              <p>{ownSessions.length ? updateLabel : 'Zodra je rijdt, verschijnen je rondes hier automatisch.'}</p>
              <button onClick={() => onNavigate('myseason')}>
                {ownSessions.length ? 'Bekijk mijn ritten' : 'Bekijk mijn seizoen'}
                <Icon name="chevronRight" size={16} />
              </button>
            </div>
            <div className="personal-dashboard-track" aria-hidden="true">
              <svg viewBox="0 0 360 150" fill="none">
                <path d="M56 42h248a34 34 0 0 1 0 68H56a34 34 0 0 1 0-68Z" />
                <path d="M56 52h248a24 24 0 0 1 0 48H56a24 24 0 0 1 0-48Z" />
                <path d="M56 32h248a44 44 0 0 1 0 88H56a44 44 0 0 1 0-88Z" />
                <circle cx="92" cy="42" r="4" />
                <circle cx="303" cy="76" r="4" />
              </svg>
            </div>
          </div>

          <div className="personal-dashboard-section-title">
            <div><h2>Jouw training</h2><span>{date ? fmtDateShort(date) : 'Vandaag'}</span></div>
            <span className={`personal-freshness ${error ? 'has-error' : ''}`} role={error ? 'status' : undefined}>
              <i />{updateLabel}
            </span>
          </div>
          <div className="personal-metrics">
            <MetricCard
              label="EIGEN RITTEN"
              value={loading || error ? '—' : ownSessions.length}
              unit={loading || error ? '' : ownSessions.length === 1 ? ' rit' : ' ritten'}
              icon="clock"
              tone="ice"
              note={loading ? 'ritten ophalen…' : error ? 'gegevens tijdelijk niet beschikbaar' : ownSessions.length ? 'op deze dag' : 'nog geen ritten gevonden'}
            />
            <MetricCard
              label="RONDEN"
              value={loading || error ? '—' : totalLaps}
              unit=""
              icon="route"
              tone="blue"
              note={loading ? 'rondes ophalen…' : error ? 'gegevens tijdelijk niet beschikbaar' : ownSessions.length ? `${transponders.length} gekoppelde ${transponders.length === 1 ? 'transponder' : 'transponders'}` : 'Automatisch bijgehouden via MyLaps'}
            />
            <MetricCard
              label="SNELSTE RONDE"
              value={loading || error ? '—' : bestLapMs ? fmtTime(bestLapMs) : '—'}
              unit={!loading && !error && bestLapMs ? ' s' : ''}
              icon="trophy"
              tone="gold"
              note={loading ? 'tijden ophalen…' : error ? 'gegevens tijdelijk niet beschikbaar' : bestLapMs ? 'van je ritten op deze dag' : 'verschijnt na je eerste ronde'}
            />
          </div>
        </>
      )}
      {ownSessions.length > 0 && (
        <div className="personal-ride-strip">
          <div className="personal-ride-date"><b>{date?.slice(8, 10) || '—'}</b><span>{date ? date.slice(5, 7) : 'RIT'}</span></div>
          <div className="personal-ride-copy"><strong>{ownSessions.length === 1 ? ownSessions[0].nickname || 'Jouw training' : `${ownSessions.length} eigen ritten`}</strong><span>{date ? fmtDateShort(date) : 'Vandaag'} · {totalLaps} ronden</span></div>
          {bestLapMs && <div className="personal-ride-best"><span>BESTE RONDE</span><b>{fmtTime(bestLapMs)} <small>s</small></b></div>}
          <button onClick={() => {
            const session = ownSessions[0];
            if (session) onNavigate('transponder', { transponder: session.transponder, date });
          }} aria-label="Open je rit"><Icon name="chevronRight" size={18} /></button>
        </div>
      )}
    </section>
  );
}
